import { useState } from "react";
import { Button } from "@/components/ui";
import { dollars, formatMoney } from "@/lib/format";
import type { Cents } from "@/lib/types";

/**
 * The recommended amount is the one figure on this screen an analyst routinely
 * changes, so it edits in place rather than behind a dialog. It commits on
 * blur or Enter, and reverts on Escape — a mis-typed loan amount should cost
 * one keystroke to undo.
 */
export function AmountField({
  amount,
  requested,
  disabled,
  onCommit,
}: {
  amount: Cents;
  requested: Cents;
  disabled: boolean;
  onCommit: (amount: Cents) => void;
}) {
  // The draft is seeded when editing starts rather than synchronised from
  // `amount` by an effect: outside an edit there is no draft to keep in step.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const commit = () => {
    setEditing(false);
    const parsed = Number(draft.replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setDraft(String(amount / 100));
      return;
    }
    const next = dollars(parsed);
    if (next !== amount) onCommit(next);
  };

  const share = Math.round((amount / requested) * 100);

  if (!editing) {
    return (
      <div>
        <div className="flex items-baseline gap-2">
          <span className="numeric text-2xl font-semibold text-ink">{formatMoney(amount)}</span>
          {!disabled ? (
            <Button
              variant="ghost"
              className="px-1.5 py-0.5 text-xs"
              onClick={() => {
                setDraft(String(amount / 100));
                setEditing(true);
              }}
            >
              Change
            </Button>
          ) : null}
        </div>
        <p className="mt-0.5 text-xs text-ink-muted">
          <span className="numeric">{share}%</span> of the {formatMoney(requested)} requested
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="text-lg text-ink-faint">$</span>
        <input
          autoFocus
          value={draft}
          inputMode="decimal"
          aria-label="Recommended amount in dollars"
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") commit();
            if (event.key === "Escape") {
              setDraft(String(amount / 100));
              setEditing(false);
            }
          }}
          className="numeric w-40 rounded-md border border-accent bg-surface px-2 py-1 text-xl font-semibold"
        />
      </div>
      <p className="mt-0.5 text-xs text-ink-faint">Enter to save · Escape to cancel</p>
    </div>
  );
}
